import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text } from "@nbs/ui";
import { formatMeasureStatusLabel } from "@/features/analytics/utils/domain-labels";
import { formatNbsNumber } from "@/features/analytics/utils/format-number";
import type { MesureMinimalFragment } from "../types/graphql";

export interface N3MesuresListProps {
  readonly mesures: readonly MesureMinimalFragment[];
  readonly notFoundCount: number;
}

/**
 * Minimal target render for `/n3?mesureIds=...` once at least one
 * `mesureId` resolved (§3.4): the raw, already-authorized fields the
 * existing `mesure(id)` query returns for each resolved measure — no
 * aggregation, no trend, no scoring, no chart, no reconstructed
 * provenance (all explicitly out of scope, §3.4/§15). Works identically
 * for exactly one measure (W2/W4 with a single `sourceMesureId`) or
 * several (W2/W4 with several) — the same table, never a special-cased
 * "single measure" layout, matching the ADR's "c'est `/n3` qui décide
 * comment présenter plusieurs mesures … pas une décision du Cockpit".
 */
export function N3MesuresList({ mesures, notFoundCount }: N3MesuresListProps) {
  const { t } = useTranslation();

  return (
    <section className="nbs-detail-panel" aria-labelledby="n3-target-mesures-heading">
      <h2 id="n3-target-mesures-heading" className="nbs-heading nbs-heading--2">
        {t("n3.target.resolvedHeading", { count: mesures.length })}
      </h2>
      <div className="nbs-catalog-table-wrap">
        <Table caption={t("n3.target.tableCaption")}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t("n3.target.columns.id")}</TableHeaderCell>
              <TableHeaderCell>{t("n3.target.columns.indicateurId")}</TableHeaderCell>
              <TableHeaderCell>{t("n3.target.columns.valeur")}</TableHeaderCell>
              <TableHeaderCell>{t("n3.target.columns.unite")}</TableHeaderCell>
              <TableHeaderCell>{t("n3.target.columns.status")}</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mesures.map((mesure) => (
              <TableRow key={mesure.id}>
                <TableCell>{mesure.id}</TableCell>
                <TableCell>{mesure.indicateurId}</TableCell>
                <TableCell>{formatNbsNumber(mesure.valeur)}</TableCell>
                <TableCell>{mesure.unite}</TableCell>
                <TableCell>{formatMeasureStatusLabel(mesure.status)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {notFoundCount > 0 ? (
        <Text size="sm" tone="subtle" role="status">
          {t("n3.target.partialNotice", { count: notFoundCount })}
        </Text>
      ) : null}
    </section>
  );
}
